import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import DeviceApproveChallenge, { ChallengeType } from '../components/auth/DeviceApproveChallenge'

/**
 * DeviceApprovePage
 *
 * Opens on the authenticated phone when it scans the device QR code.
 * URL: /device-approve?s=<sessionId>
 *
 * 1. Reads sessionId from ?s=
 * 2. Fetches session metadata from GET /api/auth/device/session?s=<sessionId>
 * 3. Renders DeviceApproveChallenge with the fetched challenge type + device info
 * 4. Shows approved / denied result screen after challenge completes
 */

interface SessionInfo {
  sessionId:     string
  challengeType: ChallengeType
  deviceHint:    string
  requestedAt:   string
  expiresAt:     number
}

type PageStatus = 'loading' | 'ready' | 'approved' | 'denied' | 'error' | 'expired' | 'locked'

const brandFont: React.CSSProperties = {
  fontFamily: '"Helvetica Neue", "HelveticaNeue-UltraLight", Helvetica, Arial, sans-serif',
}

// ─── Layout wrapper ──────────────────────────────────────────────────────────

function FullScreen({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0A0F1A',
      padding: '24px 16px',
    }}>
      <div style={{ width: '100%', maxWidth: '400px' }}>
        <p style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
          fontSize: '7px',
          letterSpacing: '0.4em',
          textTransform: 'uppercase',
          color: 'rgba(96,165,250,0.35)',
          textAlign: 'center',
          marginBottom: '24px',
        }}>
          LUKHΛS · Device Approval
        </p>
        {children}
      </div>
      <style>{`@keyframes dap-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Result card ─────────────────────────────────────────────────────────────

function ResultCard({
  icon, eyebrow, headline, body, color,
}: {
  icon: React.ReactNode
  eyebrow: string
  headline: string
  body: string
  color: string
}) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      gap: '16px', textAlign: 'center',
      padding: '40px 24px',
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '16px',
    }}>
      {icon}
      <p style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '8px', letterSpacing: '0.3em', textTransform: 'uppercase',
        color, margin: 0,
      }}>
        {eyebrow}
      </p>
      <h2 style={{
        ...brandFont, fontWeight: 100, fontSize: '22px',
        color: 'rgba(255,255,255,0.8)', margin: 0, letterSpacing: '0.02em',
      }}>
        {headline}
      </h2>
      <p style={{
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
        fontSize: '10px', letterSpacing: '0.1em', lineHeight: 1.8,
        color: 'rgba(100,116,139,0.5)', margin: 0, maxWidth: '280px',
      }}>
        {body}
      </p>
    </div>
  )
}

// ─── SVG icons ───────────────────────────────────────────────────────────────

const ApproveIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="20" stroke="rgba(96,165,250,0.4)" strokeWidth="1"/>
    <path d="M13 22l7 7 11-14" stroke="#60A5FA" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const DenyIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="20" stroke="rgba(239,68,68,0.4)" strokeWidth="1"/>
    <path d="M15 15l14 14M29 15l-14 14" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5"
          strokeLinecap="round"/>
  </svg>
)

const LockIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="20" stroke="rgba(239,68,68,0.4)" strokeWidth="1"/>
    <rect x="14" y="20" width="16" height="12" rx="2" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5"/>
    <path d="M17 20v-4a5 5 0 0110 0v4" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5"
          strokeLinecap="round"/>
  </svg>
)

const ClockIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="20" stroke="rgba(100,116,139,0.4)" strokeWidth="1"/>
    <path d="M22 14v9l5 3" stroke="rgba(100,116,139,0.6)" strokeWidth="1.5"
          strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
)

const WarnIcon = () => (
  <svg width="44" height="44" viewBox="0 0 44 44" fill="none">
    <circle cx="22" cy="22" r="20" stroke="rgba(239,68,68,0.4)" strokeWidth="1"/>
    <path d="M22 15v10M22 29v2" stroke="rgba(239,68,68,0.7)" strokeWidth="1.5"
          strokeLinecap="round"/>
  </svg>
)

// ─── Main page component ──────────────────────────────────────────────────────

export default function DeviceApprovePage() {
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get('s')

  const [status,   setStatus]   = useState<PageStatus>('loading')
  const [session,  setSession]  = useState<SessionInfo | null>(null)
  const [errorMsg, setErrorMsg] = useState('')

  useEffect(() => {
    if (!sessionId) {
      setStatus('error')
      setErrorMsg('No session ID in URL. Please scan the QR code again.')
      return
    }

    fetch(`/api/auth/device/session?s=${encodeURIComponent(sessionId)}`)
      .then(r => r.json())
      .then(data => {
        if (!data.ok) {
          if (data.error?.includes('locked'))   { setStatus('locked');  return }
          if (data.error?.includes('expired') || data.error?.includes('resolved')) {
            setStatus('expired'); return
          }
          setStatus('error')
          setErrorMsg(data.error || 'Session not found.')
          return
        }
        setSession(data)
        setStatus('ready')
      })
      .catch(() => {
        setStatus('error')
        setErrorMsg('Could not load session. Please check your connection.')
      })
  }, [sessionId])

  if (status === 'loading') {
    return (
      <FullScreen>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '20px', height: '20px',
            border: '1px solid rgba(96,165,250,0.15)',
            borderTopColor: 'rgba(96,165,250,0.6)',
            borderRadius: '50%',
            animation: 'dap-spin 0.8s linear infinite',
          }} />
          <p style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: '8px', letterSpacing: '0.3em', textTransform: 'uppercase',
            color: 'rgba(100,116,139,0.45)', margin: 0,
          }}>
            Loading session
          </p>
        </div>
      </FullScreen>
    )
  }

  if (status === 'ready' && session) {
    return (
      <FullScreen>
        <DeviceApproveChallenge
          sessionId={session.sessionId}
          challengeType={session.challengeType}
          deviceHint={session.deviceHint}
          requestedAt={session.requestedAt}
          onApproved={() => setStatus('approved')}
          onDenied={() => setStatus('denied')}
        />
      </FullScreen>
    )
  }

  if (status === 'approved') {
    return (
      <FullScreen>
        <ResultCard
          icon={<ApproveIcon />}
          eyebrow="Device approved"
          headline="Access granted"
          body="The new device has been authorised. You can close this tab."
          color="rgba(96,165,250,0.7)"
        />
      </FullScreen>
    )
  }

  if (status === 'denied') {
    return (
      <FullScreen>
        <ResultCard
          icon={<DenyIcon />}
          eyebrow="Device denied"
          headline="Access rejected"
          body="The login attempt was denied. If this was not you, your account is safe."
          color="rgba(239,68,68,0.6)"
        />
      </FullScreen>
    )
  }

  if (status === 'locked') {
    return (
      <FullScreen>
        <ResultCard
          icon={<LockIcon />}
          eyebrow="Session locked"
          headline="Too many attempts"
          body="This session has been locked after multiple failed attempts. Please start a new sign-in from the other device."
          color="rgba(239,68,68,0.6)"
        />
      </FullScreen>
    )
  }

  if (status === 'expired') {
    return (
      <FullScreen>
        <ResultCard
          icon={<ClockIcon />}
          eyebrow="Session expired"
          headline="QR code expired"
          body="This approval link has expired. Please scan the new QR code on the other device."
          color="rgba(100,116,139,0.5)"
        />
      </FullScreen>
    )
  }

  return (
    <FullScreen>
      <ResultCard
        icon={<WarnIcon />}
        eyebrow="Error"
        headline="Something went wrong"
        body={errorMsg || 'Unable to load the session. Please try again.'}
        color="rgba(239,68,68,0.6)"
      />
    </FullScreen>
  )
}
